import React, { useCallback } from 'react';
import { useDateRange, getDateRange } from '../dateRange';
import { useProperty } from './useProperty';
import useStayLength from './useStayLength';
import useRatesTags from './useRatesTags';
import useLayout from './useLayout';
import { useTravelerInterest } from '../travelerInterest';
import { useFaresApi } from './fares';
import { useBudget } from '../budget';
import { getTravelerInterest as getTravelerInterestFn } from '../../../../_integrations/everymundo/travelerInterest';

import { isNilOrEmpty, isNil } from '../../../../helpers/utils';
import { useTrackingChange } from '../../../../_integrations/everymundo/tracking/hospitality';
import { filtersReducer } from './filtersReducer';
import { useEffectReducer } from 'use-effect-reducer';
import dayjs from 'dayjs';
import { getCorrectCurrencySettings } from '../../../../helpers/utilsPricing';

function defaultResultsLength(results) {
  if (isNilOrEmpty(results)) {
    return 0;
  }
  return results.length;
}

export function useFilters({
  availableFilters = [],
  availableSecondaryFilter = [],
  filter,
  filter: {
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    maxItemsAmount,
    nextMonth = false,
    defaultMonth = 1
  },
  getFares,
  getRatesTags,
  faresTransformationLogic,
  initialData,
  rateTagsData = [],
  stayLengthData = [],
  showNoResultsPlaceholder = true,
  getResultsLength = defaultResultsLength,
  minInitialResults,
  enabledHistogram = false,
  getTravelerInterest = getTravelerInterestFn
}) {
  //TODO: Wait for validate if for the this endpoint is necessary
  const {
    outputCurrencies = [],
    priceFormat = {},
    currencySettings = [],
    currencyPricingSettings
  } = prePopulation;

  const track = useTrackingChange();

  const tracking = useCallback(
    (_, event) => {
      track(event.prevState, event.action, _);
    },
    [track]
  );

  const [state, dispatch] = useEffectReducer(
    filtersReducer,
    {
      dateRange: getDateRange(prePopulation, maxItemsAmount, nextMonth, defaultMonth),
      property: { value: prePopulation.propertyCode || '' },
      budget: (prePopulation.budget && prePopulation.budget.maximum) || null,
      stayLength: isNil(prePopulation.nightStayRange) ? {} : prePopulation.nightStayRange,
      rateTags: prePopulation.rateTags,
      firstDay: dayjs().toDate(),
      focusDate: null,
      layout: prePopulation?.layout || null,
      userInteraction: false,
      travelerInterest: {},
      stayLengthData
    },
    {
      tracking
    }
  );

  React.useEffect(() => {
    if (dayjs(state.firstDay).isBefore(state.dateRange.start)) {
      dispatch({ type: 'SET_FIRST_DAY', payload: state.dateRange.start });
      dispatch({ type: 'SET_FOCUS_DATE', payload: state.dateRange.start });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dateRange = useDateRange({ dispatch, value: state.dateRange });

  const { currencyCode, currencyToDisplay } = getCorrectCurrencySettings(
    priceFormat,
    currencyPricingSettings || currencySettings,
    outputCurrencies[0]
  );

  const budget = useBudget({
    value: state.budget,
    dispatch,
    currencyCode: currencyToDisplay || currencyCode
  });

  const property = useProperty({
    dispatch,
    value: state.property,
    filter
  });

  const isPropertySelected = Boolean(property.property.value);

  const stayLength = useStayLength({ dispatch, value: state.stayLength, stayLengthData });

  const ratesTags = useRatesTags({
    dispatch,
    getRatesTags,
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    selectedItems: state.rateTags,
    rateTagsData
  });

  const trackingDispatch = useCallback(
    (event) => {
      return dispatch({ ...event, category: 'tracking ' });
    },
    [dispatch]
  );

  const travelerInterest = useTravelerInterest({
    dispatch,
    getTravelerInterest,
    integrationSettings,
    prePopulation,
    meta,
    pageContext,
    selectedItems: state.travelerInterest
  });

  const fares = useFaresApi({
    getFares,
    prePopulation,
    integrationSettings,
    dateRange: dateRange.value,
    selectedProperty: state.property,
    stayLength: state.stayLength,
    budget: budget.value,
    rateTags: state.rateTags,
    travelerInterest: travelerInterest?.selectedItems,
    faresTransformationLogic,
    filter,
    meta,
    pageContext,
    initialData,
    showNoResultsPlaceholder,
    getResultsLength,
    minInitialResults,
    enabledHistogram,
    layout: state.layout
  });

  const layout = useLayout({
    dispatch,
    value: state.layout,
    selectedProperty: state.property,
    isLayoutActive: Boolean(state.layout)
  });

  const retry = () => {
    fares.retry();
  };

  return {
    state,
    fares,
    retry,
    dispatch,
    dateRange,
    budget,
    travelerInterest,
    layout,
    property,
    stayLength,
    ratesTags,
    availableFilters,
    availableSecondaryFilter,
    tracking: trackingDispatch,
    isPropertySelected
  };
}
